/**
 * Dialog and DropdownMenu — overlays.
 *
 * Both are built on native HTML primitives (no Radix/Headless UI) for now.
 * If we outgrow this — particularly around focus management, escape handling,
 * scroll locking — swap to Radix Dialog/DropdownMenu. The component API
 * here is designed to match Radix's signature so the swap is cheap.
 */

import {
  cloneElement,
  isValidElement,
  type MouseEvent,
  type PointerEvent,
  type ReactElement,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "../../lib/cn";
import { Icon, type IconName } from "./Icon";
import { TapTarget } from "./TapTarget";

// ---------------------------------------------------------------------------
// Dialog
// ---------------------------------------------------------------------------

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  /** Footer content — typically Cancel + primary action. */
  footer?: ReactNode;
  /** Maximum width preset. */
  size?: "sm" | "md" | "lg" | "xl";
}

const dialogSize = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
} as const;

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
}: DialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  // Sync the native <dialog>'s open state with our `open` prop. showModal()
  // provides built-in scroll lock, focus trapping, and inert-on-rest-of-page.
  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    else if (!open && el.open) el.close();
  }, [open]);

  // Clicking the ::backdrop pseudo-element bubbles up to the <dialog> node,
  // so we can detect backdrop clicks by comparing target with currentTarget.
  // Use pointerdown rather than mousedown — synthetic mouse events on touch
  // devices can race with the click that follows, occasionally swallowing
  // taps on dialog buttons under Mobile Safari/Chrome emulation.
  const onBackdrop = (e: PointerEvent<HTMLDialogElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      onCancel={(e) => {
        // Native cancel (Escape) closes the dialog; we want to route through
        // onClose() so React state stays in sync.
        e.preventDefault();
        onClose();
      }}
      onPointerDown={onBackdrop}
      // No `display` override here — the browser UA stylesheet handles
      // `dialog:not([open]) { display: none }`. If we set `display: flex`
      // we'd defeat that and the dialog would stay visible after close().
      className={cn(
        "fixed inset-0 z-50 m-0 h-full max-h-none w-full max-w-none border-0 bg-transparent p-0",
        "backdrop:bg-black/40 motion-safe:backdrop:animate-fade-in",
      )}
    >
      {/* pointer-events-none lets clicks fall through to <dialog> so we can
          detect backdrop clicks via target === currentTarget. The card itself
          re-enables pointer events. Mobil bruker mindre ytre padding (p-2) for
          a gi dialogen mer bredde uten a bli helt kant-i-kant. */}
      <div className="pointer-events-none flex h-full w-full items-end justify-center p-2 sm:items-center sm:p-6">
        <div
          className={cn(
            // overflow-hidden + min-w-0 hindrer at innhold som er bredere enn
            // dialogen stikker ut visuelt pa mobil. Innhold som virkelig trenger
            // mer bredde scroller via overflow-auto pa content-wrapperen under.
            "pointer-events-auto flex max-h-[90vh] w-full min-w-0 flex-col overflow-hidden rounded-lg bg-surface shadow-popover motion-safe:animate-scale-in",
            dialogSize[size],
          )}
        >
          {(title || description) && (
            <div className="border-ink-200 border-b px-4 py-4 sm:px-5">
              {title && <h2 className="font-semibold text-base text-ink-900">{title}</h2>}
              {description && <p className="mt-1 text-ink-500 text-sm">{description}</p>}
            </div>
          )}
          {children !== undefined && children !== null && children !== false && (
            <div className="min-h-0 min-w-0 flex-1 overflow-auto px-4 py-4 sm:px-5">{children}</div>
          )}
          {footer && (
            // `bg-surface-muted` er en semantisk alias som speiler ink-50 i
            // light og lander mellom ink-50 og surface i dark (~20/20/21) —
            // klart mørkere enn dialogen uten a ga helt mot pitch.
            <div className="flex flex-wrap items-center justify-end gap-2 rounded-b-lg border-ink-200 border-t bg-surface-muted px-4 py-3 sm:px-5">
              {footer}
            </div>
          )}
        </div>
      </div>
    </dialog>
  );
}

// ---------------------------------------------------------------------------
// DropdownMenu
// ---------------------------------------------------------------------------

export interface DropdownMenuProps {
  /**
   * The element that opens the menu. Must be a single interactive React
   * element (typically a `<button>` or our `Button`/`IconButton`) — we clone
   * it to attach the toggle handler and ARIA attributes, so wrapping a button
   * here would produce nested-button DOM.
   */
  trigger: ReactElement<{
    onClick?: (e: MouseEvent<HTMLElement>) => void;
    "aria-haspopup"?: string;
    "aria-expanded"?: boolean;
  }>;
  children: ReactNode;
  /** Align menu to start or end of trigger. */
  align?: "start" | "end";
}

export function DropdownMenu({ trigger, children, align = "end" }: DropdownMenuProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // `placement` flips between below-trigger (default) and above-trigger when
  // there isn't room below — measured against the menu's actual rendered
  // height after first paint, so it adapts to any number of items.
  const [coords, setCoords] = useState<{
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
  } | null>(null);

  // Re-measure the trigger when the menu opens so we can place the portal
  // content with `position: fixed`. Using a portal lets the menu escape
  // any clipping ancestor (table rows with overflow, Cards with rounded
  // corners, etc.) which used to swallow the menu visually.
  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    const measure = () => {
      const el = wrapperRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const menuHeight = menuRef.current?.getBoundingClientRect().height ?? 0;
      const spaceBelow = window.innerHeight - r.bottom;
      const spaceAbove = r.top;
      // Flip above when there's not enough room below but more above. The
      // 8 px buffer keeps the menu off the very edge of the viewport.
      const flipAbove = menuHeight > 0 && spaceBelow < menuHeight + 8 && spaceAbove > spaceBelow;
      const horizontal =
        align === "end" ? { right: window.innerWidth - r.right } : { left: r.left };
      setCoords(
        flipAbove
          ? { bottom: window.innerHeight - r.top + 4, ...horizontal }
          : { top: r.bottom + 4, ...horizontal },
      );
    };
    // First measure runs without a known menu height (menuRef is still
    // null) — places below. The second measure, scheduled via rAF after
    // the menu paints, sees the rendered height and can flip if needed.
    measure();
    const raf = requestAnimationFrame(measure);
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [open, align]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: globalThis.MouseEvent) => {
      const t = e.target as Node;
      if (wrapperRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const triggerOnClick = isValidElement(trigger) ? trigger.props.onClick : undefined;
  const triggerElement = isValidElement(trigger)
    ? cloneElement(trigger, {
        onClick: (e: MouseEvent<HTMLElement>) => {
          triggerOnClick?.(e);
          setOpen((v) => !v);
        },
        "aria-haspopup": "menu",
        "aria-expanded": open,
      })
    : trigger;

  // Portal target: when the trigger lives inside a modal <dialog>, the
  // dialog's `showModal()` puts its content in the browser's top layer,
  // which sits above any z-index in document.body. Portaling to body
  // would visually hide the menu behind the dialog. Falling back to the
  // nearest <dialog> ancestor when present keeps the menu in the same
  // stacking context as the trigger.
  const portalTarget =
    (wrapperRef.current?.closest("dialog") as HTMLElement | null) ?? document.body;

  return (
    <div ref={wrapperRef} className="relative inline-flex">
      {triggerElement}
      {open &&
        coords &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            // Spread the measured coords directly — measure() emits top OR
            // bottom (flip) and left OR right (align) already accounting
            // for the 4 px gap to the trigger.
            style={{ position: "fixed", ...coords }}
            className="z-50 min-w-[10rem] rounded-md border border-ink-200 bg-surface py-1 shadow-popover motion-safe:animate-scale-in"
            // Close after any menu-item click. Items live inside this
            // container; their click event bubbles up here. Keyboard
            // closure is handled via the global Escape listener above.
            onClick={() => setOpen(false)}
            onKeyDown={(e) => {
              if (e.key === "Escape" || e.key === "Enter" || e.key === " ") setOpen(false);
            }}
          >
            {children}
          </div>,
          portalTarget,
        )}
    </div>
  );
}

export interface MenuItemProps {
  icon?: IconName | undefined;
  children: ReactNode;
  onClick?: (() => void) | undefined;
  destructive?: boolean | undefined;
  disabled?: boolean | undefined;
}

export function MenuItem({ icon, children, onClick, destructive, disabled }: MenuItemProps) {
  return (
    <TapTarget
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm",
        "disabled:cursor-not-allowed",
        destructive
          ? "text-rose-700 hover:bg-rose-50 dark:text-rose-300 dark:hover:bg-rose-900/30"
          : "text-ink-700 hover:bg-ink-50",
      )}
    >
      {icon && <Icon name={icon} size="xs" />}
      {children}
    </TapTarget>
  );
}

export function MenuSeparator() {
  return <hr className="my-1 h-px border-0 bg-ink-200" />;
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return (
    <div className="px-3 py-1 font-semibold text-[11px] text-ink-500 uppercase tracking-wide">
      {children}
    </div>
  );
}
